import { graphql } from '../gql/index.ts';
import { useQuery } from 'urql';
import { SeminarCard } from './SeminarCard.tsx';
import { getFormattedSeminarDate } from './util.ts';
import { ErrorPage } from './ErrorPage.tsx';
import { ParticipantStatus } from '../gql/graphql.ts';
import { Loader } from '@progress/kendo-react-indicators';
import { useDeferredLoading } from '../shared/useDeferredLoading.ts';

const MySeminarsQuery = graphql(/* GraphQL */ `
  query getMySeminars($companyId: String!) {
    mySeminars(companyId: $companyId) {
      seminars {
        id
        title
        subtitle
        seminarNumber
        dateText
        description
        plainDescription
        locationText
        startDate
        endDate
        lecturers {
          name
        }
        categories {
          name
        }
        signUps {
          canceled
        }
        participants {
          id
          status
        }
      }
      count
    }
  }
`);

interface MySeminarListProps {
  companyId: string;
}

export const MySeminarList: React.FC<MySeminarListProps> = ({ companyId }) => {
  const [mySeminarsQueryResult] = useQuery({
    query: MySeminarsQuery,
    variables: {
      companyId,
    },
    pause: companyId === undefined || companyId === null,
  });
  const { data } = mySeminarsQueryResult;
  const { mySeminars } = data || {};

  const showLoader = useDeferredLoading(mySeminarsQueryResult.fetching);

  if (showLoader) {
    return (
      <div className="flex justify-center py-16">
        <Loader type={'infinite-spinner'} size={'large'} />
      </div>
    );
  }

  if (!mySeminarsQueryResult.data) {
    return null;
  }

  return (
    <>
      <h2>
        Meine Seminare
        {`${mySeminars?.count ? ` (${mySeminars?.count})` : ''}`}
      </h2>
      {mySeminars?.count ? (
        mySeminars.seminars?.map((seminar) => {
          return (
            <SeminarCard
              key={seminar?.id}
              instructors={
                seminar?.lecturers
                  ?.map((lecturer) => lecturer.name)
                  .filter(Boolean) as string[]
              }
              id={seminar?.seminarNumber}
              title={seminar?.title}
              time={getFormattedSeminarDate(
                seminar.startDate,
                seminar.endDate,
                seminar.dateText,
              )}
              description={seminar?.plainDescription}
              subtitle={seminar?.subtitle}
              location={seminar?.locationText}
              category={
                seminar?.categories?.find((c) => Boolean(c.name.trim()))?.name
              }
              titleLink={'/seminar/' + seminar?.id}
              isParticipationCanceled={
                seminar.participants.some(
                  (p) => p.status === ParticipantStatus.Canceled,
                ) || seminar.signUps.some((s) => s.canceled)
              }
              isWaitlist={seminar.participants.some(
                (p) => p.status === ParticipantStatus.Waitlist,
              )}
            />
          );
        })
      ) : (
        <ErrorPage text="Sie nehmen an keinen Seminaren teil" />
      )}
    </>
  );
};
